import React, { useCallback } from 'react'
import clsx from 'clsx'
import {
  RotateCcw,
  Hand,
  ZoomIn,
  ZoomOut,
  Crosshair,
  Maximize2,
  Grid3x3,
  Box,
  Gauge,
  Route,
  Flame,
  CirclePlay,
  LogOut,
} from 'lucide-react'
import { useAuth } from '../app/useAuth'
import { ToolbarButton } from './ToolbarButton'
import { useAppDispatch } from '../app/useAppDispatch'
import { useAppSelector } from '../app/useAppSelector'
import { viewerActions } from '@shared/slices/viewerSlice'
import { rtlsActions } from '@shared/slices/rtlsSlice'
import { useSceneRef } from '../scene/SceneRefContext'
import { useHistoryBounds } from '../scene/rtls/useHistoryBounds'

// One canonical size for toolbar icons. 20px reads cleanly inside the
// 44px-wide toolbar button while leaving visible padding.
const ICON_SIZE = 20

export const Toolbar: React.FC = () => {
  const dispatch = useAppDispatch()
  const sceneRefHolder = useSceneRef()
  const { profile, user, signOut } = useAuth()

  const interactionMode = useAppSelector(s => s.viewer.interactionMode)
  const showGrid = useAppSelector(s => s.viewer.showGrid)
  const showWireframe = useAppSelector(s => s.viewer.showWireframe)
  const performanceMode = useAppSelector(s => s.viewer.performanceMode)
  const fps = useAppSelector(s => s.viewer.fps)
  const showTrails = useAppSelector(s => s.rtls.showTrails)
  const heatmapEnabled = useAppSelector(s => s.rtls.heatmapEnabled)
  const playbackMode = useAppSelector(s => s.rtls.playbackMode)
  const panelFilter = useAppSelector(s => s.rtls.panelFilter)

  // Trails + Playback apply to assets that move and accumulate position
  // history — equipment and personnel (or All, which includes both).
  // APs/Locations/Chambers are static, so they stay gated off.
  const trailsAndPlaybackEnabled =
    panelFilter === 'all' ||
    panelFilter === 'equipment' ||
    panelFilter === 'personnel'
  // Bounds come from the shared hook — when an asset is selected the
  // scrubber opens at THAT asset's earliest point rather than the fleet
  // minimum (which could be unrelated to what's currently on screen).
  const { minTs, maxTs } = useHistoryBounds()
  const hasHistory = maxTs > minTs

  const handleEnterPlayback = useCallback(() => {
    if (!hasHistory) return

    if (playbackMode === 'live') {
      dispatch(rtlsActions.setPlaybackMode('playback'))
      dispatch(rtlsActions.setPlaybackTimestamp(minTs))
      dispatch(rtlsActions.setPlaybackPlaying(false))
    } else {
      // Clicking the toolbar button while in playback returns to live.
      dispatch(rtlsActions.setPlaybackMode('live'))
    }
  }, [dispatch, playbackMode, minTs, hasHistory])

  // Camera mode helpers: dispatch Redux AND call sceneRef so controls update immediately
  const handleOrbit = () => {
    dispatch(viewerActions.setInteractionMode('orbit'))
    sceneRefHolder.current?.camera.setMode('orbit')
  }
  const handlePan = () => {
    dispatch(viewerActions.setInteractionMode('pan'))
    sceneRefHolder.current?.camera.setMode('pan')
  }

  const section = 'flex flex-col items-center gap-xs py-sm'
  const divider = 'h-px w-full bg-surfaceBorder opacity-60'

  return (
    <aside
      className="flex h-full w-toolbar min-w-toolbar flex-col items-center border-r border-surfaceBorder bg-surface py-sm"
      aria-label="Camera controls toolbar"
    >
      {/* Camera controls — merged "mode / zoom / actions" per UX review
          so operators don't hunt across three separated zones. Internal
          button order follows workflow frequency. */}
      <div className={section}>
        <ToolbarButton
          icon={<RotateCcw size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={handleOrbit}
          active={interactionMode === 'orbit'}
          title="Orbit mode"
        />
        <ToolbarButton
          icon={<Hand size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={handlePan}
          active={interactionMode === 'pan'}
          title="Pan mode"
        />
        <ToolbarButton
          icon={<ZoomIn size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => sceneRefHolder.current?.camera.zoomIn()}
          title="Zoom in"
        />
        <ToolbarButton
          icon={<ZoomOut size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => sceneRefHolder.current?.camera.zoomOut()}
          title="Zoom out"
        />
        <ToolbarButton
          icon={<Crosshair size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => sceneRefHolder.current?.camera.reset()}
          title="Reset camera"
        />
        <ToolbarButton
          icon={<Maximize2 size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => sceneRefHolder.current?.camera.fitToModel()}
          title="Fit to model"
        />
      </div>
      <div className={divider} aria-hidden="true" />

      {/* View toggles */}
      <div className={section}>
        <ToolbarButton
          icon={<Grid3x3 size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => dispatch(viewerActions.toggleGrid())}
          active={showGrid}
          title="Toggle grid"
        />
        <ToolbarButton
          icon={<Box size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => dispatch(viewerActions.toggleWireframe())}
          active={showWireframe}
          title="Toggle wireframe"
        />
        <ToolbarButton
          icon={<Gauge size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => dispatch(viewerActions.togglePerformanceMode())}
          active={performanceMode}
          title="Toggle performance mode"
        />
      </div>
      <div className={divider} aria-hidden="true" />

      {/* Analytics overlays */}
      <div className={section}>
        <ToolbarButton
          icon={<Route size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => dispatch(rtlsActions.toggleTrails())}
          active={showTrails}
          disabled={!trailsAndPlaybackEnabled}
          title={
            trailsAndPlaybackEnabled
              ? 'Toggle trails'
              : 'Trails are only available for Equipment / Personnel / All'
          }
        />
        <ToolbarButton
          icon={<Flame size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={() => dispatch(rtlsActions.toggleHeatmap())}
          active={heatmapEnabled}
          title="Toggle heatmap"
        />
      </div>
      <div className={divider} aria-hidden="true" />

      {/* Playback — promoted to its own section per UX review. Playback
          is a mode switch (not a visibility toggle) so it lives below
          the overlay toggles, closer to the bottom-of-panel status. */}
      <div className={section}>
        <ToolbarButton
          icon={<CirclePlay size={ICON_SIZE} strokeWidth={1.75} />}
          onClick={handleEnterPlayback}
          active={playbackMode === 'playback'}
          disabled={!trailsAndPlaybackEnabled}
          title={
            !trailsAndPlaybackEnabled
              ? 'Playback is only available for Equipment / Personnel / All'
              : playbackMode === 'playback'
                ? 'Return to live'
                : 'Playback history'
          }
        />
      </div>

      {/* Account + FPS pinned to the bottom. Display label falls through
          users.display_name → personnel.name → email prefix → 'Account'. */}
      <div className={clsx('mt-auto flex flex-col items-center', 'pb-sm text-center')}>
        <button
          type="button"
          onClick={() => void signOut()}
          title={`Sign out${user?.email ? ` (${user.email})` : ''}`}
          aria-label="Sign out"
          className={clsx(
            'mb-md flex h-9 w-9 items-center justify-center rounded-md',
            'border border-surfaceBorder text-textSecondary',
            'hover:border-statusRed hover:text-statusRed',
          )}
        >
          <LogOut size={16} strokeWidth={1.75} />
        </button>
        <span className="mb-sm max-w-[60px] truncate text-[10px] text-textMuted" title={user?.email ?? undefined}>
          {(() => {
            if (profile?.display_name) return profile.display_name
            if (profile?.personnel_name) return profile.personnel_name
            const email = user?.email ?? ''
            if (!email) return 'Account'
            const prefix = email.split('@')[0]
            return prefix.length > 9 ? prefix.slice(0, 8) + '…' : prefix
          })()}
        </span>
        <span className="text-xs text-textMuted">FPS</span>
        <span className="text-sm font-semibold text-textPrimary tabular-nums">
          {fps ?? 0}
        </span>
      </div>
    </aside>
  )
}
